import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const EMPTY_EVENT = {
  title: "Lomond Operator Academy Assessment Day",
  date: "", start_time: "09:00", end_time: "15:30",
  location_name: "Lomond Plant, Falkirk", address: "",
  arrival_minutes: 15, capacity: 8, instructor: "",
  simulator_included: true, practical_included: true, interview_included: true,
  notes: "", candidate_instructions: "", parking_instructions: "", travel_info: "",
  what_to_bring: "", ppe_requirements: "", contact_name: "", contact_number: "",
  status: "open", repeat_weeks: 1,
};

const inputCls = "bg-white border-steel text-charcoal h-10";

function F({ label, children, wide }) {
  return (
    <div className={`space-y-1.5 ${wide ? "sm:col-span-2" : ""}`}>
      <Label className="text-slate-600 text-xs uppercase tracking-wider font-semibold">{label}</Label>
      {children}
    </div>
  );
}

export default function EventForm({ open, onClose, event, onSaved }) {
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(event && event.id);

  useEffect(() => {
    if (open) setForm(event && event.id ? { ...EMPTY_EVENT, ...event } : { ...EMPTY_EVENT, ...(event || {}) });
  }, [open, event]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title || !form.date) { toast.error("Title and date are required."); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/events/${event.id}`, form);
      else await api.post("/admin/events", form);
      toast.success(isEdit ? "Assessment day updated." : "Assessment day created.");
      onClose();
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-steel text-foreground max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-charcoal">
            {isEdit ? "Edit Assessment Day" : "Create Assessment Day"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Event title" wide>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="ef-title" />
          </F>
          <F label="Date">
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="ef-date" />
          </F>
          <F label="Candidate arrival (mins before start)">
            <Input type="number" value={form.arrival_minutes} onChange={(e) => set("arrival_minutes", Number(e.target.value))} className={inputCls} data-testid="ef-arrival" />
          </F>
          <F label="Start time">
            <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className={inputCls} data-testid="ef-start" />
          </F>
          <F label="End time">
            <Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className={inputCls} data-testid="ef-end" />
          </F>
          <F label="Location name">
            <Input value={form.location_name} onChange={(e) => set("location_name", e.target.value)} className={inputCls} data-testid="ef-location" />
          </F>
          <F label="Full address">
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="ef-address" />
          </F>
          <F label="Maximum capacity">
            <Input type="number" min={1} value={form.capacity} onChange={(e) => set("capacity", Number(e.target.value))} className={inputCls} data-testid="ef-capacity" />
          </F>
          <F label="Assigned instructor / assessor">
            <Input value={form.instructor} onChange={(e) => set("instructor", e.target.value)} className={inputCls} data-testid="ef-instructor" />
          </F>
          {!isEdit && (
            <F label="Repeat weekly">
              <Select value={String(form.repeat_weeks)} onValueChange={(v) => set("repeat_weeks", Number(v))}>
                <SelectTrigger className={inputCls} data-testid="ef-repeat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 6, 8].map((n) => <SelectItem key={n} value={String(n)}>{n === 1 ? "One-off" : `Weekly × ${n}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          )}
          <F label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className={inputCls} data-testid="ef-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["draft", "open", "closed", "completed", "cancelled"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <div className="sm:col-span-2 flex flex-wrap gap-6 bg-elevated rounded-md p-4">
            {[["simulator_included", "Simulator"], ["practical_included", "Practical aptitude"], ["interview_included", "Interview"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-charcoal cursor-pointer" data-testid={`ef-${k}`}>
                <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} /> {l}
              </label>
            ))}
          </div>
          <F label="Event notes (internal)" wide>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-notes" />
          </F>
          <F label="Candidate instructions" wide>
            <Textarea value={form.candidate_instructions} onChange={(e) => set("candidate_instructions", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-candidate-instructions" />
          </F>
          <F label="What to bring">
            <Textarea value={form.what_to_bring} onChange={(e) => set("what_to_bring", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-bring" />
          </F>
          <F label="PPE / clothing requirements">
            <Textarea value={form.ppe_requirements} onChange={(e) => set("ppe_requirements", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-ppe" />
          </F>
          <F label="Parking instructions">
            <Textarea value={form.parking_instructions} onChange={(e) => set("parking_instructions", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-parking" />
          </F>
          <F label="Travel information">
            <Textarea value={form.travel_info} onChange={(e) => set("travel_info", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="ef-travel" />
          </F>
          <F label="Contact person">
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} data-testid="ef-contact-name" />
          </F>
          <F label="Contact number">
            <Input value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} className={inputCls} data-testid="ef-contact-number" />
          </F>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-steel text-charcoal bg-white" data-testid="ef-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="ef-save">
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Assessment Day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
